import type { ComponentType } from "react";
import { trackLazyRoute } from "./lazyRoutePending";

type DefaultComponentModule<Props extends object> = {
  default: ComponentType<Props>;
};

type NoProps = Record<string, never>;

type LazyRouteResult = {
  Component: ComponentType<NoProps>;
};

/**
 * Creates a React Router lazy route loader for page modules with a default
 * component export. Props can be passed for routes that bind variants or
 * parent types at route-definition time.
 */
export function lazyRoute<Props extends object = NoProps>(
  importer: () => Promise<DefaultComponentModule<Props>>,
  props?: Props,
) {
  return async (): Promise<LazyRouteResult> => {
    const module = await trackLazyRoute(importer);
    const Component = module.default;

    if (!props) {
      return { Component: Component as ComponentType<NoProps> };
    }

    return {
      Component: () => <Component {...props} />,
    };
  };
}
